import h from 'hyperscript';

import type { Sidebar } from 'ui/sidebar/sidebar';

import type { EventKey } from 'cadenza/utils/event-util';
import { on, unByKey } from 'cadenza/utils/event-util';
import { deleteUrlParam, setParam } from 'cadenza/utils/url-param-utils';
import {
  APPLY_FILTERS,
  CHANGED,
  CONDITION_CHANGE,
  CONDITIONS_HIERARCHIES_CHANGE
} from 'cadenza/conditions/event-types';
import { HIERARCHY_CHANGE } from 'cadenza/hierarchy-selection/event-types';
import { EXECUTION_CONFIRMED } from 'cadenza/api-client/api-constants';
import { mediaXsOnly } from 'cadenza/utils/media';
import { bindHotkey, getFormattedHotkey, unbindHotkey } from 'cadenza/hotkeys/hotkeys';
import { HOTKEYS } from 'cadenza/hotkeys/hotkey-definitions';
import type {
  ConditionChangeEventDetails,
  FilterController
} from 'cadenza/conditions/filter-controller';

import './on-demand-view.css';
import i18n from './on-demand-view.properties';

const CLASS_NAME = 'd-on-demand-view';
const COVER_VIEW_CLASS_NAME = 'd-on-demand-canceled-cover';

const ON_DEMAND_UPDATE_CLASS_SELECTOR = '.d-on-demand-view--update';
const ON_DEMAND_FIRST_LOAD_SELECTOR = '.d-on-demand-view--first-load';
const REPEAT_CANCELED_CLASS_SELECTOR = '.d-on-demand-view--repeatCanceled';

type OnDemandState =
  /** nothing loaded yet */
  'NEW'
  /** displaying something but already loading something new */
  | 'OUTDATED'
  /** latest request are cancelled */
  | 'CANCELED';

export class OnDemandView extends HTMLElement {

  #refreshHandler?: () => void;
  #filterController?: FilterController;
  #eventListenerKeys: EventKey[] = [];
  #cancelState = new CancelState(this);
  // ! – initialized in `connectedCallback`
  #changedHierarchies!: Set<string>;
  // ! – initialized in `connectedCallback`
  #changedConditions!: Set<string>;
  #conditionValuesSetHash?: string;

  // ui
  // ! – all initialized in `connectedCallback`
  #firstLoadHeader!: HTMLSpanElement;
  #updateHeader!: HTMLSpanElement;
  #repeatCanceledHeader!: HTMLSpanElement;
  #firstLoadButton!: HTMLButtonElement;
  #updateButton!: HTMLButtonElement;
  #repeatCanceledButton!: HTMLButtonElement;

  get conditionValuesSetHash () {
    return this.#conditionValuesSetHash;
  }

  /**
   * onDemand mode means that every filter change has to be confirmed.
   * If false, the OnDemandView is only displayed after a manual cancel via button.
   *
   * @return true if we are in on-demand mode, false otherwise
   */
  get #onDemandMode () {
    return this.cancelOnly !== 'true' || mediaXsOnly.matches;
  }

  get cancelOnly () {
    // ! – because it's guaranteed to be there
    return this.getAttribute('cancel-only')!;
  }

  get hiddenElementSelector () {
    // ! – because it's guaranteed to be there
    return this.getAttribute('hidden-element-selector')!;
  }

  get #state () {
    return this.dataset.state as OnDemandState || 'NEW';
  }

  set #state (state: OnDemandState) {
    this.dataset.state = state;
  }

  _setStateOutdated () {
    this.#state = 'OUTDATED';
  }

  connectedCallback () {
    // allow to accumulate information carried by throttled events, when execution is confirmed it must be cleaned
    this.#changedHierarchies = new Set();
    this.#changedConditions = new Set();

    this.classList.add(CLASS_NAME, COVER_VIEW_CLASS_NAME);

    if (this.#onDemandMode) {
      const executionConfirmed = this.#executionConfirmed;
      this.hidden = executionConfirmed;
      if (executionConfirmed) {
        this._setStateOutdated();
      }
    } else {
      this.hidden = true;
    }

    this.#registerEvents(this._filterController || this);
    this.append(this.#createUiContent());
    this.#activateKeyListener();
    this.#registerMobileControlsEvents();
  }

  disconnectedCallback () {
    unbindHotkey(HOTKEYS.SELECTORS_UPDATE_ON_DEMAND_VIEW);
  }

  #registerMobileControlsEvents () {
    if (!this.#onDemandMode || !this._filterController) {
      return;
    }
    const applyButton = document.querySelector('.button-primary.mobile-filter');
    const cancelButton = document.querySelector('.button-secondary.mobile-filter');
    if (!applyButton || !cancelButton) {
      return;
    }
    // ! – because it's guaranteed to be there
    const sidebarGetter = () => this._filterController!.closest<Sidebar>('.d-sidebar')!;
    this.#eventListenerKeys.push(
      on(applyButton, 'click', _ => {
        this.#onLoadData();
        sidebarGetter().close();
      }),
      on(cancelButton, 'click', _ => sidebarGetter().close())
    );
  }

  #activateKeyListener () {
    bindHotkey({
      hotkeyId: HOTKEYS.SELECTORS_UPDATE_ON_DEMAND_VIEW,
      callback: (event: Event) => {
        event.preventDefault();
        if (!this.hidden) {
          this.#onLoadData();
        }
      }
    });
  }

  /**
   * If the filter controller is not available yet when the OnDemandView is added to the DOM, it can
   * be set explicitly later with this method.
   *
   * @param filterController - filterController to use as event source
   */
  setFilterController (filterController: FilterController) {
    this.#filterController = filterController;
    unByKey(this.#eventListenerKeys);
    this.#registerEvents(this.#filterController);
    this.#registerMobileControlsEvents();
  }

  get _filterController () {
    return this.#filterController || document.querySelector<FilterController>('.d-filter-controller')!;
  }

  #registerEvents (eventSource: HTMLElement) {
    this.#eventListenerKeys = [
      on(eventSource, [
        CONDITION_CHANGE,
        HIERARCHY_CHANGE,
        CONDITIONS_HIERARCHIES_CHANGE
      ], (event: CustomEvent) => this.#filterCriteriaChanged(event)),
      on(eventSource, APPLY_FILTERS, () => this.#onLoadData()),
      on(document, 'keydown', (event: KeyboardEvent) => {
        if (event.ctrlKey && event.key === 'Enter') {
          this.#onLoadData();
          event.preventDefault();
        }
      })
    ];
  }

  setEnabled (enabled: boolean) {
    [ ...this.querySelectorAll('button') ]
      .forEach(button => (button.disabled = !enabled));
  }

  handleCanceledRequest (refreshHandler: () => void, conditionValueSetHash: string | null) {
    this.#cancelState.requestCanceled(conditionValueSetHash);
    // ignore cancel, if we already have a new request
    if (!this.#cancelState.canceled) {
      return;
    }
    this.#state = 'CANCELED';
    this.#refreshHandler = refreshHandler;
    this.hidden = false;
  }

  get #executionConfirmed () {
    return new URLSearchParams(location.search).get(EXECUTION_CONFIRMED) === 'true';
  }

  #createUiContent () {
    this.#firstLoadHeader = h('span.headline' + ON_DEMAND_FIRST_LOAD_SELECTOR, i18n('onDemand.initial.text'));
    this.#updateHeader = h('span.headline' + ON_DEMAND_UPDATE_CLASS_SELECTOR, i18n('onDemand.update.text'));
    this.#repeatCanceledHeader = h('span.headline' + REPEAT_CANCELED_CLASS_SELECTOR, i18n('repeatCanceled.text'));
    this.#firstLoadButton = h('button.button.button-primary' + ON_DEMAND_FIRST_LOAD_SELECTOR, {
      type: 'button',
      title: i18n('onDemand.initial.btn'),
      onclick: () => this.#onLoadData(false) // do not mark as outdated on initial load
    }, i18n('onDemand.initial.btn'));
    this.#updateButton = h('button.button.button-primary' + ON_DEMAND_UPDATE_CLASS_SELECTOR, {
      type: 'button',
      title: i18n('onDemand.update.btn'),
      onclick: () => this.#onLoadData()
    }, i18n('onDemand.update.btn'));
    this.#repeatCanceledButton = h('button.button.button-primary' + REPEAT_CANCELED_CLASS_SELECTOR, {
      type: 'button',
      title: i18n('repeatCanceled.btn'),
      onclick: () => {
        this.#onLoadData();
        this.#cancelState.reset();
      }
    }, i18n('repeatCanceled.btn'));

    return h('span#confirmExecutionContainer',
      h('div#confirmExecutionOverlay.dimmed-backdrop',
        this.#firstLoadHeader,
        this.#updateHeader,
        this.#repeatCanceledHeader,
        this.#firstLoadButton,
        this.#updateButton,
        this.#repeatCanceledButton,
        h('span.text-muted', `(${getFormattedHotkey(HOTKEYS.SELECTORS_UPDATE_ON_DEMAND_VIEW)})`)
      ));
  }

  #filterCriteriaChanged (event: CustomEvent) {
    if (event.type === CONDITION_CHANGE) {
      this.#changedConditions.add(event.detail.conditionId);
      return;
    }
    if (event.type === HIERARCHY_CHANGE) {
      this.#changedHierarchies.add(event.detail.name);
      return;
    }
    // the event can contain an oldConditionValuesSetHash if a condition changed, but not if a hierarchy changed
    const oldConditionValuesSetHash = event.detail.oldConditionValuesSetHash || this.#conditionValuesSetHash || this.#getConditionValuesSetHash();
    if (event.detail.conditionValuesSetHash) {
      this.#conditionValuesSetHash = event.detail.conditionValuesSetHash;
    }

    if (!event.cancelable) {
      this.hidden = true;
      return;
    }

    event.detail.hierarchyNames.forEach((value: string) => this.#changedHierarchies.add(value));
    event.detail.conditionIds.forEach((value: string) => this.#changedConditions.add(value));

    if (this.#onDemandMode) {
      deleteUrlParam(EXECUTION_CONFIRMED);
      this.hidden = false;
      this.#cancelState.requestCanceled(oldConditionValuesSetHash);
      this._setStateOutdated();

      event.preventDefault();
    }
  }

  /**
   * On loading data
   *
   * @param markAsOutdated - whether this leaves the NEW state.
   * False for the initial request, true for filterChanges and loading changed filters.
   * @param oldConditionValuesSetHash - the conditionValuesSetHash before condition changes
   */
  #onLoadData (markAsOutdated = true, oldConditionValuesSetHash = null) {
    if (this.#onDemandMode) {
      setParam({ paramName: EXECUTION_CONFIRMED, paramValue: 'true' });
    }
    if (this.#state === 'NEW') {
      this.showHiddenElement();
      this.#dispatchChangeEvent(oldConditionValuesSetHash);
    } else if (this.#state === 'OUTDATED') {
      // in case user loads view, changes filter and then loads data for the first time
      this.showHiddenElement();
      this.#dispatchChangeEvent(oldConditionValuesSetHash);
    } else /* if (this._state === State.CANCELED) */ {
      if (this.#cancelState.canceled) {
        // enable going back to this state later
        this.#cancelState.reset();
        this.#refresh();
      }
    }
    if (markAsOutdated) {
      this._setStateOutdated();
    }
  }

  /**
   * Hide overlay and execute refresh handler.
   */
  #refresh () {
    this.hidden = true;
    // refresh the sourrounding settings
    this.#refreshHandler!();
    this.#refreshHandler = undefined;
  }

  #getConditionValuesSetHash () {
    return this.#conditionValuesSetHash || (this._filterController ? this._filterController.conditionValuesSetHash : null);
  }

  #dispatchChangeEvent (oldConditionValuesSetHash = null) {
    const eventTarget = this._filterController || this;
    const eventDetail = {
      conditionValuesSetHash: this.#getConditionValuesSetHash(),
      conditionIds: Array.from(this.#changedConditions),
      hierarchyNames: Array.from(this.#changedHierarchies),
      ...oldConditionValuesSetHash ? { oldConditionValuesSetHash } : null
    };
    eventTarget.dispatchEvent(new CustomEvent<ConditionChangeEventDetails>(CONDITIONS_HIERARCHIES_CHANGE, { detail: eventDetail }));
    eventTarget.dispatchEvent(new CustomEvent<ConditionChangeEventDetails>(CHANGED, { detail: eventDetail }));

    this.#changedConditions.clear();
    this.#changedHierarchies.clear();
  }

  showHiddenElement () {
    // ! – because it's guaranteed to be there
    const hiddenElement = document.querySelector<HTMLElement>(this.hiddenElementSelector)!;
    if (hiddenElement) {
      hiddenElement.hidden = false;
    }
  }

}

class CancelState {

  _canceledHashes = new Set();
  _onDemandView;

  constructor (onDemandView: OnDemandView) {
    this._onDemandView = onDemandView;
  }

  get canceled () {
    return this._canceledHashes.has(this._onDemandView.conditionValuesSetHash);
  }

  requestCanceled (conditionValuesSetHash: string | null) {
    this._canceledHashes.add(conditionValuesSetHash || this._onDemandView.conditionValuesSetHash);
  }

  reset () {
    this._canceledHashes = new Set();
  }

}

customElements.define(CLASS_NAME, OnDemandView);

/**
 * Show the overlay as cancelled, if the conditions are in the state
 * that is canceled.
 *
 * @param refreshHandler - function to call to refresh the outer site
 * @param conditionValueSetHash - hash of the cancelled request at the
 * time the request was started. May be null when the dialog was
 * cancelled from an interactive element (then the current
 * conditionValueSetHash is used).
 */
export function displayOnDemandViewWithStateCanceled (refreshHandler: () => void, conditionValueSetHash: string | null) {
  getOnDemandViewElement().handleCanceledRequest(refreshHandler, conditionValueSetHash);
}

/**
 *  explicitly mark the on demand view as outdated, because the
 * loading is finished so any change cannot be new anymore.
 */
export function setOnDemandViewStateForFinishedLoading () {
  getOnDemandViewElement()?._setStateOutdated();
}

export function getOnDemandViewElement () {
  return document.getElementsByTagName('d-on-demand-view')[0] as OnDemandView;
}
